/**
 * R2 — Source drill-down side-pane. Opens when the operator clicks the
 * CovBadge on the MediaCoverageSection. Does NOT dismiss the outbreak
 * detail panel — both stay on screen so the operator keeps context.
 *
 * Per CD spec:
 *   - default sort = credibility (T1 official leads; T3 aggregators sink)
 *   - filter by source type (all / official / mainstream / aggregator)
 *   - header carries the confidence split (N high · M ambiguous mapping)
 *   - ambiguous items DEMOTED (dashed border, dimmed) but NEVER HIDDEN
 *   - per-row: cred badge + date + headline + source + sentiment + (if
 *     ambiguous) "≈ ambiguous link" tag explaining why
 */

import { useMemo, useState } from 'react';
import { CredBadge, SentimentChip } from './primitives';
import { ArticleLink } from './ArticleLink';
import type { CoverageArticle, ArticleType } from './types';

type SortMode = 'cred' | 'date' | 'type';
type FilterMode = 'all' | ArticleType;

export interface SourcePaneProps {
  outbreakLabel: string;
  articles: readonly CoverageArticle[];
  onClose: () => void;
}

export function SourcePane({ outbreakLabel, articles, onClose }: SourcePaneProps) {
  const [sort, setSort] = useState<SortMode>('cred');
  const [filter, setFilter] = useState<FilterMode>('all');

  const rows = useMemo(() => {
    let r = articles.slice();
    if (filter !== 'all') r = r.filter(a => a.type === filter);
    // Primary sort by chosen mode.
    r.sort((a, b) => {
      if (sort === 'cred') return a.cred - b.cred;
      if (sort === 'date') return Date.parse(b.date) - Date.parse(a.date);
      return a.type.localeCompare(b.type);
    });
    // Then ambiguous items ALWAYS sink within their sort group (honesty —
    // visible but demoted; never silently dropped).
    r.sort((a, b) => (a.conf === 'ambiguous' ? 1 : 0) - (b.conf === 'ambiguous' ? 1 : 0));
    return r;
  }, [articles, sort, filter]);

  const high = articles.filter(a => a.conf === 'high').length;
  const amb = articles.filter(a => a.conf === 'ambiguous').length;

  return (
    <aside
      role="dialog"
      aria-label={`Sources for ${outbreakLabel}`}
      style={{
        width: 380, maxWidth: '95vw',
        background: 'var(--bm-bg)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        boxShadow: 'var(--bm-shadow-lg)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '80vh',
      }}
    >
      <header style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--bm-border)',
      }}>
        <div>
          <div style={{ fontWeight: 650, fontSize: 14 }}>Sources · {outbreakLabel}</div>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>
            {articles.length} items · {high} high-confidence · {amb} ambiguous mapping
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close source list"
          style={{
            background: 'transparent', border: '1px solid var(--bm-border)',
            color: 'var(--bm-text-muted)', borderRadius: 6,
            width: 28, height: 28, cursor: 'pointer', fontSize: 16, lineHeight: 1,
          }}
        >×</button>
      </header>

      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: 8,
        padding: '10px 14px', borderBottom: '1px solid var(--bm-border)', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'inline-flex', border: '1px solid var(--bm-border)', borderRadius: 6 }}>
          {([['cred', 'credibility'], ['date', 'date'], ['type', 'type']] as const).map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setSort(k)}
              style={{
                padding: '3px 9px',
                fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
                border: 'none', cursor: 'pointer',
                background: sort === k ? 'var(--bm-bg-strong)' : 'transparent',
                color: sort === k ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
              }}
            >{l}</button>
          ))}
        </div>
        <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
          {([['all', 'All'], ['official', 'Official'], ['mainstream', 'Mainstream'], ['aggregator', 'Aggregator']] as const).map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k as FilterMode)}
              style={{
                padding: '2px 8px', borderRadius: 999,
                fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 600,
                border: `1px solid ${filter === k ? 'var(--bm-text-muted)' : 'var(--bm-border)'}`,
                background: filter === k ? 'var(--bm-bg-subtle)' : 'transparent',
                color: filter === k ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
                cursor: 'pointer',
              }}
            >{l}</button>
          ))}
        </div>
      </div>

      <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
        {rows.map(a => (
          <article
            key={a.id}
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid var(--bm-border)',
              opacity: a.conf === 'ambiguous' ? 0.7 : 1,
              borderLeft: a.conf === 'ambiguous' ? '2px dashed var(--bm-text-subtle)' : '2px solid transparent',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginBottom: 6 }}>
              <CredBadge cred={a.cred} />
              <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>
                {a.date.slice(0, 10)}
              </span>
            </div>
            {/* Headline is rendered through ArticleLink — the ONLY sanctioned
                external-link path. It applies the safeArticleHref scheme guard
                (poisoned/malformed → non-clickable text), always emits
                rel="noopener noreferrer", and flags unverified (T3/unknown)
                sources. The CredBadge above still carries the tier; ArticleLink
                adds the bare domain + the click-through. */}
            <div style={{ marginBottom: 6 }}>
              <ArticleLink article={a} />
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              fontSize: 11, color: 'var(--bm-text-subtle)',
            }}>
              <SentimentChip value={a.sentiment} />
              {a.conf === 'ambiguous' && (
                <span
                  title="Outbreak→article link is uncertain (geocoding / pathogen-name match below threshold)"
                  style={{
                    fontFamily: 'var(--bm-font-mono)', fontSize: 10,
                    color: 'var(--bm-text-subtle)',
                    border: '1px dashed var(--bm-text-subtle)',
                    borderRadius: 3,
                    padding: '0 5px',
                  }}
                >≈ ambiguous link</span>
              )}
            </div>
          </article>
        ))}
      </div>

      <footer style={{
        padding: '10px 14px', borderTop: '1px solid var(--bm-border)',
        fontSize: 11, color: 'var(--bm-text-subtle)', lineHeight: 1.5,
      }}>
        Ambiguous-mapping items are demoted, never hidden — operator decides.
        No editorial line: tone is observed, not judged.
        {' '}Links open in a new tab with no referrer; only <code>http</code>/<code>https</code> links
        are clickable, and <strong>unverified</strong> (aggregator / unknown) sources are flagged.
        Links are <strong>not malware-scanned</strong> — a tier label rates the publisher, not the
        link. (Planned: ingest-time URL-reputation check, e.g. Google Safe Browsing / abuse.ch URLhaus.)
      </footer>
    </aside>
  );
}
